
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ConnectionContextType {
  isOnline: boolean;
  isSupabaseConnected: boolean;
}

const ConnectionContext = createContext<ConnectionContextType | undefined>(undefined);

const INITIAL_RECONNECT_DELAY = 5000; // 5 seconds
const MAX_RECONNECT_DELAY = 30000;    // 30 seconds
const MAX_SILENT_RETRIES = 2;
const BACKOFF_FACTOR = 1.5;

export function ConnectionProvider({ children }: { children: React.ReactNode }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSupabaseConnected, setIsSupabaseConnected] = useState(true);
  const [showConnectionAlert, setShowConnectionAlert] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const reconnectTimeoutRef = useRef<number>();
  const currentChannelRef = useRef<ReturnType<typeof supabase.channel>>();
  const { toast } = useToast();

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      window.clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = undefined;
    }
    if (currentChannelRef.current) {
      supabase.removeChannel(currentChannelRef.current);
      currentChannelRef.current = null;
    }
  }, []);

  const getReconnectDelay = useCallback(() => {
    const delay = Math.min(
      INITIAL_RECONNECT_DELAY * Math.pow(BACKOFF_FACTOR, reconnectAttempts),
      MAX_RECONNECT_DELAY
    );
    return delay + (Math.random() * 1000); // Add jitter
  }, [reconnectAttempts]);

  const handleReconnection = useCallback(async () => {
    if (!isOnline || !navigator.onLine) return;

    cleanup();

    try {
      console.log(`Attempting reconnection (attempt ${reconnectAttempts + 1})`);
      
      const channel = supabase.channel('system');
      
      currentChannelRef.current = channel;
      
      const handleConnectionChange = async (status: string) => {
        const isConnected = status === 'SUBSCRIBED';
        console.log(`Connection status changed to: ${status}`);
        
        setIsSupabaseConnected(isConnected);
        
        if (isConnected) {
          if (reconnectAttempts > MAX_SILENT_RETRIES) {
            toast({
              title: "Connection Restored",
              description: "Database connection has been re-established",
            });
          }
          setReconnectAttempts(0);
          setShowConnectionAlert(false);
        } else {
          setReconnectAttempts(prev => prev + 1);
          
          if (reconnectAttempts > MAX_SILENT_RETRIES) {
            setShowConnectionAlert(true);
            toast({
              title: "Database Connection Lost",
              description: "Attempting to reconnect...",
              variant: "destructive",
            });
          }

          const delay = getReconnectDelay();
          console.log(`Scheduling next reconnection attempt in ${Math.round(delay/1000)}s`);
          reconnectTimeoutRef.current = window.setTimeout(handleReconnection, delay);
        }
      };

      channel
        .on('error', (error) => {
          console.error('Channel error:', error);
          setIsSupabaseConnected(false);
          
          if (reconnectAttempts > MAX_SILENT_RETRIES) {
            setShowConnectionAlert(true);
          }
        })
        .on('SUBSCRIBED', () => handleConnectionChange('SUBSCRIBED'))
        .on('CLOSED', () => handleConnectionChange('CLOSED'))
        .on('CHANNEL_ERROR', () => handleConnectionChange('ERROR'))
        .subscribe(async (status) => {
          if (status === 'SUBSCRIBED') {
            // Verify the connection by making a simple query
            const { error } = await supabase.from('schools').select('count').single();
            if (error) {
              console.error('Connection verification failed:', error);
              handleConnectionChange('ERROR');
            }
          }
        });

    } catch (error) {
      console.error('Error in reconnection attempt:', error);
      setReconnectAttempts(prev => prev + 1);
      
      const delay = getReconnectDelay();
      console.log(`Reconnection attempt failed. Next attempt in ${Math.round(delay/1000)}s`);
      reconnectTimeoutRef.current = window.setTimeout(handleReconnection, delay);
    }
  }, [isOnline, reconnectAttempts, toast, getReconnectDelay, cleanup]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      handleReconnection();
      toast({
        title: "Connection Restored",
        description: "Internet connection is back online",
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsSupabaseConnected(false);
      setShowConnectionAlert(true);
      cleanup();
      
      toast({
        title: "Connection Lost",
        description: "Please check your internet connection",
        variant: "destructive",
      });
    };

    // Add visibility change handler
    const handleVisibilityChange = () => {
      if (!document.hidden && navigator.onLine) {
        console.log('Page became visible, checking connection...');
        handleReconnection();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial connection setup
    handleReconnection();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      cleanup();
    };
  }, [handleReconnection, toast, cleanup]);

  return (
    <ConnectionContext.Provider value={{ isOnline, isSupabaseConnected }}>
      {showConnectionAlert && reconnectAttempts > MAX_SILENT_RETRIES && (
        <Alert 
          variant="destructive" 
          className="fixed top-4 right-4 z-50 max-w-md animate-in fade-in slide-in-from-top-2 shadow-lg"
        >
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Database Connection Lost</AlertTitle>
          <AlertDescription>
            Connection to the database has been lost. Attempting to reconnect...
            {reconnectAttempts > MAX_SILENT_RETRIES && ` (Attempt ${reconnectAttempts - MAX_SILENT_RETRIES})`}
          </AlertDescription>
        </Alert>
      )}
      {children}
    </ConnectionContext.Provider>
  );
}

export function useConnection() {
  const context = useContext(ConnectionContext);
  if (context === undefined) {
    throw new Error("useConnection must be used within a ConnectionProvider");
  }
  return context;
}
